import React, { useMemo } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Avatar,
  Box,
  Chip,
  Divider,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import {
  computeChatSessionMetrics,
  isAgentMessage,
  METRICS_UNAVAILABLE,
  pickSessionString,
  visitorPresenceFromStatus,
} from '../../../lib/chatSessionMetrics';

function InfoRow({ label, value, multiline = false }) {
  const display = value == null || value === '' ? METRICS_UNAVAILABLE : String(value);
  return (
    <Stack
      direction={multiline ? 'column' : 'row'}
      justifyContent="space-between"
      spacing={multiline ? 0.5 : 2}
      sx={{ py: 0.75 }}
    >
      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
        {label}
      </Typography>
      {multiline && display.startsWith('http') ? (
        <Link
          href={display}
          target="_blank"
          rel="noopener noreferrer"
          variant="caption"
          sx={{ fontWeight: 500, wordBreak: 'break-all' }}
        >
          {display}
        </Link>
      ) : (
        <Typography
          variant="caption"
          sx={{
            fontWeight: 500,
            textAlign: multiline ? 'left' : 'right',
            wordBreak: 'break-word',
          }}
        >
          {display}
        </Typography>
      )}
    </Stack>
  );
}

/**
 * Right column — visitor profile, participants, and live session metadata.
 */
export default function ChatInfoPanel({
  session = null,
  messages = [],
  messagesLoading = false,
  sessionLabel = () => 'Visitor',
  agentDisplayName = 'Unassigned',
}) {
  const metrics = useMemo(
    () => computeChatSessionMetrics(messages, session, { loading: messagesLoading }),
    [messages, session, messagesLoading],
  );

  const resolvedAgentName = useMemo(() => {
    if (agentDisplayName && agentDisplayName !== 'Unassigned') return agentDisplayName;
    const sorted = [...messages].filter((m) => m?.isInternal !== true);
    const agentMsg = sorted.find(isAgentMessage);
    if (agentMsg?.senderLabel && agentMsg.senderLabel !== 'Support') {
      return agentMsg.senderLabel;
    }
    return 'Unassigned';
  }, [agentDisplayName, messages]);

  if (!session) {
    return (
      <Paper
        variant="outlined"
        square
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          minHeight: 0,
          borderRadius: 0,
          borderTop: 0,
          borderBottom: 0,
          borderRight: 0,
          p: 3,
        }}
      >
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Select a conversation to view visitor and session details.
        </Typography>
      </Paper>
    );
  }

  const visitorName = sessionLabel(session).split(' (')[0] || 'Visitor';
  const visitorEmail =
    typeof session.visitorEmail === 'string' && session.visitorEmail.trim()
      ? session.visitorEmail.trim()
      : METRICS_UNAVAILABLE;
  const presence = visitorPresenceFromStatus(session.status);
  const profileLocation = pickSessionString(session, [
    'visitorLocation',
    'profileLocation',
    'location',
    'visitorProfileLocation',
  ]);
  const ipAddress = pickSessionString(session, [
    'visitorIp',
    'visitorIpAddress',
    'ipAddress',
    'clientIp',
  ]);
  const backendPanelUrl = pickSessionString(session, ['backendPanelUrl', 'crmProfileUrl']);

  return (
    <Paper
      variant="outlined"
      square
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        height: '100%',
        borderRadius: 0,
        borderTop: 0,
        borderBottom: 0,
        borderRight: 0,
        overflow: 'auto',
      }}
    >
      <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&::before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <PersonOutlineIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              User details
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.main', fontSize: '1.25rem' }}>
              {visitorName.charAt(0).toUpperCase()}
            </Avatar>
            <Box textAlign="center">
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                {visitorName}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {visitorEmail}
              </Typography>
            </Box>
            <Chip size="small" label={presence.label} color={presence.color} variant="outlined" />
          </Stack>

          <Stack spacing={0.5}>
            {profileLocation ? <InfoRow label="Profile location" value={profileLocation} /> : null}
            {ipAddress ? <InfoRow label="IP address" value={ipAddress} /> : null}
          </Stack>

          {backendPanelUrl ? (
            <Box sx={{ mt: 2 }}>
              <Link
                href={backendPanelUrl}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                sx={{ fontWeight: 700 }}
              >
                Open backend panel
              </Link>
            </Box>
          ) : null}
        </AccordionDetails>
      </Accordion>

      <Divider />

      <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&::before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <GroupsOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Conversation participants
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.light' }}>
                {visitorName.charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {visitorName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Visitor
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'grey.300' }}>
                <SupportAgentOutlinedIcon fontSize="small" />
              </Avatar>
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {resolvedAgentName}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Assigned agent
                </Typography>
              </Box>
            </Stack>
          </Stack>
        </AccordionDetails>
      </Accordion>

      <Divider />

      <Accordion defaultExpanded disableGutters elevation={0} sx={{ '&::before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon fontSize="small" color="action" />
            <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
              Chat info
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Stack divider={<Divider flexItem />}>
            <InfoRow label="Session Reference" value={metrics.sessionReference} />
            <InfoRow label="Active Engagement Time" value={metrics.activeEngagementTime} />
            <InfoRow label="Initial Time-to-Reply (TTR)" value={metrics.initialTtr} />
            <InfoRow label="Mean Agent Latency" value={metrics.meanAgentLatency} />
            <InfoRow label="Peak Wait Time" value={metrics.peakWaitTime} />
            <InfoRow label="Engagement Frequency" value={metrics.engagementFrequency} />
            <InfoRow
              label="Originating Node"
              value={metrics.originatingNode}
              multiline={metrics.originatingNode?.startsWith?.('http')}
            />
            <InfoRow label="Geographic Consistency" value={metrics.geographicConsistency} />
            <InfoRow label="User Local Time" value={metrics.userLocalTime} />
          </Stack>
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
}
